
import { Link } from 'react-router-dom';

export default function SystemThinkingPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-6 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium mb-6">
              システム思考（全体最適）
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              なぜ「良かれと思った改善」が、<br />薬局を苦しくするのか
            </h1>
            <p className="text-2xl md:text-3xl font-light opacity-95">
              ― 部分最適は、全体を必ず壊す ―
            </p>
          </div>
        </div>
      </section>

      {/* Conclusion Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-10 rounded-2xl shadow-xl">
              <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
                <i className="ri-lightbulb-flash-line text-4xl"></i>
                結論
              </h2>
              <p className="text-xl leading-relaxed">
                調剤薬局が行き詰まる最大の理由は、個々の工程を良くしすぎていることです。必要なのは「速い人」でも「優秀な工程」でもなく、全体として流れる設計です。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Theory Explanation */}
      <section className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-gray-900">理論の解説｜システム思考とは何か</h2>
            
            <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                システム思考とは、物事を「要素の集合」ではなく、相互に影響し合う"ひとつの仕組み"として捉える考え方です。
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                この分野を体系化した人物として有名なのが、<strong className="text-purple-600">ピーター・センゲ</strong> です。
              </p>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-l-4 border-purple-600 p-6 rounded-lg">
                <p className="text-xl font-semibold text-gray-900 mb-2">システム思考の核心は、これです。</p>
                <p className="text-lg text-gray-800 leading-relaxed">
                  全体の振る舞いは、個々の能力ではなく<br />
                  要素同士の<strong className="text-purple-600">"つながり方"</strong>で決まる。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partial Optimization Trap */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-gray-900">調剤薬局で起きている「部分最適の罠」</h2>
            <p className="text-lg text-gray-700 mb-8">現場でよくある"善意の改善"を見てみましょう。</p>

            <div className="space-y-6">
              {/* Case 1 */}
              <div className="bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-500 p-8 rounded-xl shadow-md">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="bg-red-500 text-white w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold">1</span>
                  ケース① 調剤を速くした
                </h3>
                <div className="flex items-center gap-3 text-lg text-gray-800 flex-wrap">
                  <span className="bg-white px-4 py-2 rounded-lg shadow-sm">調剤完了が早くなる</span>
                  <i className="ri-arrow-right-line text-red-500 text-2xl"></i>
                  <span className="bg-white px-4 py-2 rounded-lg shadow-sm">鑑査待ちが山積み</span>
                  <i className="ri-arrow-right-line text-red-500 text-2xl"></i>
                  <span className="bg-white px-4 py-2 rounded-lg shadow-sm">投薬が詰まる</span>
                  <i className="ri-arrow-right-line text-red-500 text-2xl"></i>
                  <span className="bg-red-100 px-4 py-2 rounded-lg shadow-sm font-semibold">待ち時間増加・ミスリスク上昇</span>
                </div>
              </div>

              {/* Case 2 */}
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border-l-4 border-orange-500 p-8 rounded-xl shadow-md">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="bg-orange-500 text-white w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold">2</span>
                  ケース② 受付を増やした
                </h3>
                <div className="flex items-center gap-3 text-lg text-gray-800 flex-wrap">
                  <span className="bg-white px-4 py-2 rounded-lg shadow-sm">処方箋を多く受ける</span>
                  <i className="ri-arrow-right-line text-orange-500 text-2xl"></i>
                  <span className="bg-white px-4 py-2 rounded-lg shadow-sm">WIPが急増</span>
                  <i className="ri-arrow-right-line text-orange-500 text-2xl"></i>
                  <span className="bg-orange-100 px-4 py-2 rounded-lg shadow-sm font-semibold">鑑査・投薬が崩壊</span>
                </div>
              </div>

              {/* Case 3 */}
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-l-4 border-yellow-600 p-8 rounded-xl shadow-md">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="bg-yellow-600 text-white w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold">3</span>
                  ケース③ 全員を忙しくした
                </h3>
                <div className="flex items-center gap-3 text-lg text-gray-800 flex-wrap">
                  <span className="bg-white px-4 py-2 rounded-lg shadow-sm">手が空いた人に仕事を振る</span>
                  <i className="ri-arrow-right-line text-yellow-600 text-2xl"></i>
                  <span className="bg-white px-4 py-2 rounded-lg shadow-sm">制約工程が見えなくなる</span>
                  <i className="ri-arrow-right-line text-yellow-600 text-2xl"></i>
                  <span className="bg-yellow-100 px-4 py-2 rounded-lg shadow-sm font-semibold">全体の流れが悪化</span>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-gradient-to-r from-purple-100 to-indigo-100 p-6 rounded-xl border-2 border-purple-300">
              <p className="text-xl font-semibold text-gray-900 flex items-start gap-3">
                <i className="ri-arrow-right-circle-fill text-purple-600 text-2xl mt-1"></i>
                <span>どれも"その場では正しい"が、全体では逆効果。</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Happens */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-gray-900">なぜこうなるのか</h2>
            
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <p className="text-lg text-gray-700 mb-4">理由は単純です。</p>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3 text-lg">
                  <i className="ri-checkbox-circle-fill text-purple-600 text-2xl"></i>
                  <span className="text-gray-800">薬局は<strong className="text-purple-600">受付 → 調剤 → 鑑査 → 投薬</strong>という"直列システム"</span>
                </div>
                <div className="flex items-center gap-3 text-lg">
                  <i className="ri-checkbox-circle-fill text-purple-600 text-2xl"></i>
                  <span className="text-gray-800">直列では、<strong className="text-purple-600">一部を速くしても全体は速くならない</strong></span>
                </div>
              </div>

              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-6 rounded-xl border-l-4 border-red-500">
                <p className="text-lg text-gray-800 mb-3">むしろ、</p>
                <ul className="space-y-2 text-lg text-gray-800">
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-1">•</span>
                    <span>速くした工程が</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-1">•</span>
                    <span>次工程の負荷を増やし</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-1">•</span>
                    <span>システム全体を不安定にします。</span>
                  </li>
                </ul>
              </div>

              <div className="mt-6 bg-purple-100 p-6 rounded-xl">
                <p className="text-xl font-semibold text-gray-900">
                  これが、「改善しているのに楽にならない」正体です。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What to Change */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-gray-900">システム思考で見ると、何を変えるべきか</h2>
            
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-8 rounded-2xl shadow-xl mb-8">
              <p className="text-2xl font-semibold mb-2">システム思考では、次の問いを常に立てます。</p>
              <p className="text-3xl font-bold">「この改善は、次の工程に何を起こすか？」</p>
            </div>

            <div className="bg-white border-2 border-purple-200 rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-hospital-line text-purple-600 text-3xl"></i>
                調剤薬局での翻訳
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-purple-50 rounded-xl">
                  <i className="ri-arrow-right-circle-fill text-purple-600 text-2xl mt-1"></i>
                  <div>
                    <p className="text-lg text-gray-800">
                      <strong>調剤を速くする前に</strong>→ 鑑査・投薬は耐えられるか？
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-purple-50 rounded-xl">
                  <i className="ri-arrow-right-circle-fill text-purple-600 text-2xl mt-1"></i>
                  <div>
                    <p className="text-lg text-gray-800">
                      <strong>受付を増やす前に</strong>→ WIPは制御できるか？
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-gradient-to-r from-purple-100 to-indigo-100 p-6 rounded-xl border-2 border-purple-300">
                <p className="text-xl font-semibold text-gray-900 flex items-start gap-3">
                  <i className="ri-arrow-right-circle-fill text-purple-600 text-2xl mt-1"></i>
                  <span>"次工程目線"がない改善はしない。</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Relationship with Other Theories */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-gray-900">TOC・DBR・フロー効率との関係</h2>
            <p className="text-lg text-gray-700 mb-8">ここで、これまでの理論が一本につながります。</p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white border-2 border-blue-200 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                    <i className="ri-focus-3-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">TOC</h3>
                </div>
                <p className="text-gray-700">全体を決める制約を見る</p>
              </div>

              <div className="bg-white border-2 border-green-200 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <i className="ri-stop-circle-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">DBR</h3>
                </div>
                <p className="text-gray-700">入れすぎを止める</p>
              </div>

              <div className="bg-white border-2 border-teal-200 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center">
                    <i className="ri-time-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">フロー効率</h3>
                </div>
                <p className="text-gray-700">待ちを減らす</p>
              </div>

              <div className="bg-white border-2 border-orange-200 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                    <i className="ri-rhythm-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">タクト思考</h3>
                </div>
                <p className="text-gray-700">需要の波に合わせる</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-8 rounded-2xl shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="ri-links-line text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold">システム思考</h3>
              </div>
              <p className="text-xl">それらを"全体として整合"させる</p>
              <div className="mt-6 bg-white/20 backdrop-blur-sm p-4 rounded-xl">
                <p className="text-lg flex items-start gap-3">
                  <i className="ri-arrow-right-circle-fill text-2xl mt-1"></i>
                  <span>システム思考は、これまでの理論を束ねる<strong>"上位視点"</strong>です。</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Points */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-gray-900">薬局マネジメントでの実装ポイント</h2>

            <div className="space-y-6">
              {/* Point 1 */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-l-4 border-purple-600 p-8 rounded-xl shadow-md">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="bg-purple-600 text-white w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold">1</span>
                  評価指標を「個人」から「流れ」へ
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-600 mb-2 font-semibold">❌ 従来の指標</p>
                    <div className="space-y-2">
                      <div className="bg-white px-4 py-2 rounded-lg text-gray-700 line-through">枚数</div>
                      <div className="bg-white px-4 py-2 rounded-lg text-gray-700 line-through">スピード</div>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-2 font-semibold">✅ システム思考の指標</p>
                    <div className="space-y-2">
                      <div className="bg-purple-100 px-4 py-2 rounded-lg text-gray-900 font-semibold">リードタイム</div>
                      <div className="bg-purple-100 px-4 py-2 rounded-lg text-gray-900 font-semibold">WIP</div>
                      <div className="bg-purple-100 px-4 py-2 rounded-lg text-gray-900 font-semibold">待ち時間</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Point 2 */}
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-l-4 border-blue-600 p-8 rounded-xl shadow-md">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="bg-blue-600 text-white w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold">2</span>
                  改善は「一箇所ずつ」
                </h3>
                <p className="text-lg text-gray-700 mb-4">同時多発的に変えない。</p>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="bg-white px-5 py-3 rounded-lg shadow-sm text-gray-800 font-medium">1つ変える</span>
                  <i className="ri-arrow-right-line text-blue-600 text-2xl"></i>
                  <span className="bg-white px-5 py-3 rounded-lg shadow-sm text-gray-800 font-medium">全体の反応を見る</span>
                  <i className="ri-arrow-right-line text-blue-600 text-2xl"></i>
                  <span className="bg-white px-5 py-3 rounded-lg shadow-sm text-gray-800 font-medium">次を決める</span>
                </div>
                <div className="mt-4 bg-blue-100 p-4 rounded-lg">
                  <p className="text-gray-800 flex items-start gap-2">
                    <i className="ri-arrow-right-circle-fill text-blue-600 text-xl mt-1"></i>
                    <span>システムは、必ず反作用を起こす。</span>
                  </p>
                </div>
              </div>

              {/* Point 3 */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-600 p-8 rounded-xl shadow-md">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="bg-green-600 text-white w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold">3</span>
                  "楽になったか"を判断基準にする
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-lg text-gray-800">
                    <i className="ri-checkbox-circle-fill text-green-600 text-2xl"></i>
                    <span>現場が落ち着いたか</span>
                  </div>
                  <div className="flex items-center gap-3 text-lg text-gray-800">
                    <i className="ri-checkbox-circle-fill text-green-600 text-2xl"></i>
                    <span>投薬の質が上がったか</span>
                  </div>
                  <div className="flex items-center gap-3 text-lg text-gray-800">
                    <i className="ri-checkbox-circle-fill text-green-600 text-2xl"></i>
                    <span>患者対応に余白ができたか</span>
                  </div>
                </div>
                <div className="mt-4 bg-green-100 p-4 rounded-lg">
                  <p className="text-gray-800">これらは、全体最適が進んでいるサインです。</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* KPI Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-gray-900">KPI｜システム思考で見る指標</h2>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white border-2 border-purple-200 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center">
                    <i className="ri-time-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">全体リードタイム</h3>
                </div>
                <p className="text-gray-600">受付から投薬までの総時間</p>
              </div>

              <div className="bg-white border-2 border-blue-200 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                    <i className="ri-stack-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">制約前後のWIP</h3>
                </div>
                <p className="text-gray-600">仕掛品の滞留状況</p>
              </div>

              <div className="bg-white border-2 border-teal-200 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center">
                    <i className="ri-timer-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">時間帯別の待ち時間</h3>
                </div>
                <p className="text-gray-600">ピーク時の負荷状況</p>
              </div>

              <div className="bg-white border-2 border-orange-200 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                    <i className="ri-pause-circle-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">投薬中断回数</h3>
                </div>
                <p className="text-gray-600">集中力の維持状況</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-100 to-indigo-100 p-6 rounded-xl border-2 border-purple-300">
              <p className="text-xl font-semibold text-gray-900 flex items-start gap-3">
                <i className="ri-arrow-right-circle-fill text-purple-600 text-2xl mt-1"></i>
                <span>個人KPIより、流れKPIを優先。</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tomorrow's Action */}
      <section className="py-16 bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-block p-4 bg-white/20 backdrop-blur-sm rounded-full mb-4">
                <i className="ri-rocket-line text-5xl"></i>
              </div>
              <h2 className="text-4xl font-bold mb-4">明日の一手｜システム思考の第一歩</h2>
              <p className="text-xl opacity-90">明日やることは、これだけです。</p>
            </div>

            <div className="bg-white/10 backdrop-blur-md border-2 border-white/30 p-10 rounded-2xl shadow-2xl">
              <div className="flex items-start gap-4 mb-6">
                <i className="ri-arrow-right-circle-fill text-4xl text-yellow-300 mt-1"></i>
                <p className="text-2xl font-bold leading-relaxed">
                  「この改善は、次工程に何を起こすか？」を<br />口に出してから実行する。
                </p>
              </div>

              <div className="bg-white/20 backdrop-blur-sm p-6 rounded-xl mt-6">
                <p className="text-lg mb-3">それだけで、</p>
                <ul className="space-y-2 text-lg">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-yellow-300 text-xl"></i>
                    <span>無駄な改善</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-yellow-300 text-xl"></i>
                    <span>事故につながる忙しさ</span>
                  </li>
                </ul>
                <p className="text-lg mt-3 font-semibold">が、確実に減ります。</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-gray-900 text-center">関連する理論</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Link
                to="/theory/toc"
                className="group bg-gradient-to-br from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border-2 border-blue-200"
              >
                <div className="flex items-center justify-center w-14 h-14 bg-blue-500 rounded-full mb-4 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-focus-3-line text-white text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">TOC（制約理論）</h3>
                <p className="text-sm text-gray-600">全体を決める制約を見る</p>
              </Link>

              <Link
                to="/theory/dbr"
                className="group bg-gradient-to-br from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border-2 border-green-200"
              >
                <div className="flex items-center justify-center w-14 h-14 bg-green-500 rounded-full mb-4 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-stop-circle-line text-white text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">DBR</h3>
                <p className="text-sm text-gray-600">入れすぎを止める</p>
              </Link>

              <Link
                to="/theory/flow-efficiency"
                className="group bg-gradient-to-br from-teal-50 to-teal-100 hover:from-teal-100 hover:to-teal-200 p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border-2 border-teal-200"
              >
                <div className="flex items-center justify-center w-14 h-14 bg-teal-500 rounded-full mb-4 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-time-line text-white text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">フロー効率</h3>
                <p className="text-sm text-gray-600">待ちを減らす</p>
              </Link>

              <Link
                to="/theory/takt-thinking"
                className="group bg-gradient-to-br from-orange-50 to-orange-100 hover:from-orange-100 hover:to-orange-200 p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border-2 border-orange-200"
              >
                <div className="flex items-center justify-center w-14 h-14 bg-orange-500 rounded-full mb-4 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-rhythm-line text-white text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">タクト思考</h3>
                <p className="text-sm text-gray-600">需要の波に合わせる</p>
              </Link>
            </div>

            <div className="mt-8 text-center">
              <Link
                to="/theory"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line text-xl"></i>
                理論一覧に戻る
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
